import { config } from '@iheartjane/config';
import { Button, Flex, Modal, Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import styles from './EmbeddedRowPreview.module.css';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'jane-menu-row': DetailedHTMLProps<
        HTMLAttributes<HTMLElement> & {
          preview?: string;
          'row-id'?: string;
        },
        HTMLElement
      >;
    }
  }
}

interface EmbeddedRowPreviewProps {
  menuRowId: number;
  menuRowType: string;
  menuUrl: string;
  onClose: () => void;
  open: boolean;
  storeId: number;
}

export const EmbeddedRowPreview = ({
  menuRowId,
  menuRowType,
  menuUrl,
  onClose,
  open,
  storeId,
}: EmbeddedRowPreviewProps) => {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const { control } = useFormContext();
  const header = useWatch({ control, name: 'header' });
  const colors = useWatch({ control, name: 'colors' });
  const fonts = useWatch({ control, name: 'fonts' });

  useEffect(() => {
    const src = config.embeddedMenuRowScriptUrl;
    if (
      customElements.get('jane-menu-row') ||
      document.querySelector(`script[src="${src}"]`)
    ) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    document.head.appendChild(script);
  }, []);

  const previewJson = useMemo(
    () =>
      JSON.stringify({
        header,
        colors: colors ?? {},
        menu_row_type: menuRowType,
        menu_row_id: menuRowId,
        fonts: {
          heading: fonts?.heading
            ? {
                font_family: fonts.heading.family,
                url: fonts.heading.files?.['regular'],
              }
            : undefined,
          body: fonts?.body
            ? {
                font_family: fonts.body.family,
                url: fonts.body.files?.['regular'],
              }
            : undefined,
        },
        menu_url: menuUrl,
        store_id: storeId,
      }),
    [header, colors, fonts, menuUrl, storeId]
  );

  return (
    <Modal
      appId="embedded-row-modal"
      className={styles['modal']}
      isLayered
      onRequestClose={onClose}
      open={open}
    >
      <Modal.Header
        title="Preview"
        centerContent={
          <Flex gap={8}>
            <Button
              label="Desktop"
              onClick={() => setViewMode('desktop')}
              variant={viewMode === 'desktop' ? 'secondary' : 'tertiary'}
            />
            <Button
              label="Mobile"
              onClick={() => setViewMode('mobile')}
              variant={viewMode === 'mobile' ? 'secondary' : 'tertiary'}
            />
          </Flex>
        }
      />
      <Modal.Content>
        <Typography color="text-light" mb={24}>
          This row displays full-width on desktop. Add-to-bag isn&apos;t
          available from this view—shoppers can click View all or any product
          card to enter your menu. Since the embedded row background is
          transparent, be sure your text and price colors remain readable on the
          page background where you plan to place it.
        </Typography>

        <div
          className={clsx(styles['preview-container'], {
            [styles['preview-container-mobile']]: viewMode === 'mobile',
          })}
          data-testid="preview-container"
        >
          <div className={styles['header-placeholder']}>
            <div className={styles['header-pill']} />
          </div>

          <jane-menu-row preview={previewJson} row-id="0" />
        </div>
      </Modal.Content>
    </Modal>
  );
};
